import type { JsonLike } from '#src/json.ts'

// An observation node's `mode` when it shows the participant's latest value
// for its code instead of capturing one. Absent means it captures.
export const OBSERVATION_MODE_LATEST = 'latest'

// Ties the Observations of one multi-select capture together. Time does not:
// each answer is recorded on its own click. Search
// `identifier=<system>|<group id>`.
export const OBSERVATION_ANSWER_SET_IDENTIFIER_SYSTEM =
  'https://www.seenhealth.org/fhir/identifiers/observation-answer-set'

// The grouping key on an Observation, when it belongs to a multi-select capture.
export function observationAnswerSetId(
  identifier: { system?: string; value?: string; use?: string }[] | undefined
): string | null {
  const match = identifier?.find(
    (id) =>
      id.system === OBSERVATION_ANSWER_SET_IDENTIFIER_SYSTEM &&
      id.use !== 'old' &&
      !!id.value
  )
  return match?.value ?? null
}

export interface ObservationAnswerSet<T> {
  // Absent when the observation stands alone.
  groupId: string | null
  members: T[]
}

// Newest-first observations, collapsed so one multi-select capture is one
// entry. Observations with no grouping key stay on their own.
export function groupByAnswerSet<T>(
  items: readonly T[],
  answerSetId: (item: T) => string | null
): ObservationAnswerSet<T>[] {
  const groups: ObservationAnswerSet<T>[] = []
  const indexByGroup = new Map<string, number>()
  for (const item of items) {
    const groupId = answerSetId(item)
    if (!groupId) {
      groups.push({ groupId: null, members: [item] })
      continue
    }
    const existing = indexByGroup.get(groupId)
    if (existing === undefined) {
      indexByGroup.set(groupId, groups.length)
      groups.push({ groupId, members: [item] })
    } else {
      groups[existing]?.members.push(item)
    }
  }
  return groups
}

// One answer recorded by a repeating choice: its Observation, and the answer
// code, so the control can show what is selected without reading the record.
export interface RecordedObservationAnswer {
  reference: string
  code: string
}

export function recordedObservationAnswers(
  value: JsonLike | undefined
): RecordedObservationAnswer[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return []
    }
    const record = item as { reference?: unknown; code?: unknown }
    if (
      typeof record.reference !== 'string' ||
      !record.reference.includes('/') ||
      typeof record.code !== 'string' ||
      record.code.length === 0
    ) {
      return []
    }
    return [{ reference: record.reference, code: record.code }]
  })
}

// `data-references` as stored on the node. Malformed JSON costs the list,
// not the document that carries it.
export function parseRecordedObservationAnswers(
  raw: string | null | undefined
): RecordedObservationAnswer[] {
  if (!raw) {
    return []
  }
  try {
    return recordedObservationAnswers(JSON.parse(raw))
  } catch {
    return []
  }
}

// Every Observation a node records: the single `reference`, and each answer
// of a repeating choice. None for a node frozen to the latest value (`mode`
// "latest"): it shows whatever was recorded last and records nothing, so
// removing or cutting it never acts on a record. (Freezing also moves its
// own reference aside, to `frozenReference`; this guards HTML written
// otherwise.)
export function observationReferenceStrings(attrs: {
  reference?: string | null
  references?: JsonLike | undefined
  mode?: JsonLike | undefined
}): string[] {
  if (attrs.mode === OBSERVATION_MODE_LATEST) {
    return []
  }
  const listed = recordedObservationAnswers(attrs.references).map(
    (answer) => answer.reference
  )
  return [
    ...new Set([...(attrs.reference ? [attrs.reference] : []), ...listed]),
  ]
}
